import React, { useState, useEffect, useContext, useMemo } from "react";
import { Grid, Whisper, Tooltip } from "rsuite";
import { toast } from "react-toastify";
import TabsHT from "../../../components/tabs-ht/TabsHT";
import { PropertyStoreContext } from "../../../contexts/PropertyStoreContext";
import { getCancellationPolicies, getSeasonalPolicies } from "../../../api";
import PropertyConfigHeader from "../PropertyConfigHeader";
import PropertyPageCard from "../PropertyPageCard";
import PropertyConfigTable from "../PropertyConfigTable";
import { getNextSortDirection } from "../tableUtils";
import { POLICY_TABS } from "./constants";
import {
  formatCancellationWindow,
  formatCancelPaymentAmount,
  formatPolicyDateRanges,
  getPolicyRatePlans,
  filterPoliciesBySearch,
  sortPoliciesByStatus,
} from "./utils";
import "../propertyConfig.scss";
import "./policies.scss";

/**
 * First item inline, the rest behind a hovered +N badge. The tooltip lists
 * every item — including the one already inline — so it reads as the row's
 * complete list rather than just the badge's remainder.
 *
 * Hover-only matches every other Whisper in the app; no tooltip here is
 * click-to-open, so there is no mobile pattern to mirror.
 */
const OverflowListCell = ({ items }) => {
  const [firstItem, ...rest] = items ?? [];

  // Every branch returns an element. The empty case used to return the bare
  // string "-", which made the return type string | Element for no benefit —
  // React renders both the same, but a caller could not treat the result
  // uniformly (and it trips the same-return-type lint).
  if (firstItem === undefined) {
    return <span className="policies-page__overflow-text">-</span>;
  }

  // A lone item needs no flex row or badge, just the ellipsis + native tooltip.
  if (rest.length === 0) {
    return (
      <span className="policies-page__overflow-text ellipsis" title={firstItem}>
        {firstItem}
      </span>
    );
  }

  return (
    <div className="policies-page__overflow-cell">
      <span className="policies-page__overflow-text">{firstItem}</span>
      <Whisper
        placement="top"
        trigger="hover"
        enterable
        speaker={
          <Tooltip className="policies-page__tooltip">
            <ul className="policies-page__tooltip-list">
              {items.map((item, index) => (
                <li key={`${item}-${index}`}>{item}</li>
              ))}
            </ul>
          </Tooltip>
        }
      >
        <span className="policies-page__overflow-count">+{rest.length}</span>
      </Whisper>
    </div>
  );
};

const getActiveWindowItems = (windows, formatter) => {
  if (!windows || windows.length === 0) return [];
  const activeWindows = windows.filter((w) => w.isActive);
  return activeWindows.map(formatter);
};

const getCancellationColumns = (sortDirection, onSort, currency) => [
  { key: "policyName", label: "Name", className: "policies-page__col-name" },
  {
    key: "isActive",
    label: "Status",
    className: "policies-page__col-status",
    headerClassName: "policies-page__col-status-header",
    sortable: true,
    sortDirection,
    onSort,
    render: (row) => (
      <span
        className={`policies-page__status-badge${
          !row.isActive ? " policies-page__status-badge--inactive" : ""
        }`}
      >
        {row.isActive ? "Active" : "Inactive"}
      </span>
    ),
  },
  { key: "crsCode", label: "Code", className: "policies-page__col-code" },
  {
    key: "cancellationWindow",
    label: "Cancellation Window",
    className: "policies-page__col-window",
    render: (row) => (
      <OverflowListCell
        items={getActiveWindowItems(
          row.htCancellationWindows,
          formatCancellationWindow
        )}
      />
    ),
  },
  {
    key: "amount",
    label: "Amount",
    className: "policies-page__col-amount",
    render: (row) => (
      <OverflowListCell
        items={getActiveWindowItems(row.htCancellationWindows, (w) =>
          formatCancelPaymentAmount(w, currency)
        )}
      />
    ),
  },
  {
    key: "attachedRateplans",
    label: "Active Rate Plans",
    className: "policies-page__col-plans",
    render: (row) =>
      row.attachedRateplans?.length > 0
        ? row.attachedRateplans.map((plan) => (
            <span key={plan} className="policies-page__plan-badge">
              {plan}
            </span>
          ))
        : "-",
  },
];

const getSeasonalColumns = (sortDirection, onSort, currency) => [
  { key: "policyName", label: "Name", className: "policies-page__col-name" },
  {
    key: "isActive",
    label: "Status",
    className: "policies-page__col-status",
    headerClassName: "policies-page__col-status-header",
    sortable: true,
    sortDirection,
    onSort,
    render: (row) => (
      <span
        className={`policies-page__status-badge${
          !row.isActive ? " policies-page__status-badge--inactive" : ""
        }`}
      >
        {row.isActive ? "Active" : "Inactive"}
      </span>
    ),
  },
  {
    // Replaces the old Start Date / End Date pair: a policy can apply to
    // several ranges, and two columns can only ever show one of them. Matches
    // the Launchpad display so the same policy reads the same in both products.
    key: "dateRange",
    label: "Date Range",
    className: "policies-page__col-date-range",
    render: (row) => (
      <OverflowListCell items={formatPolicyDateRanges(row.seasonalPolicies)} />
    ),
  },
  {
    key: "amount",
    label: "Amount",
    className: "policies-page__col-amount",
    render: (row) => (
      <OverflowListCell
        items={getActiveWindowItems(row.htCancellationWindows, (w) =>
          formatCancelPaymentAmount(w, currency)
        )}
      />
    ),
  },
  {
    key: "ratePlans",
    label: "Rate Plans",
    className: "policies-page__col-plans",
    render: (row) => {
      // Union across ranges: attachedRateplans now sits on each date range, so
      // reading just the first would hide a plan used only by a later season.
      const plans = getPolicyRatePlans(row.seasonalPolicies);

      // Both branches return a fragment. This used to return the bare string
      // "-" or a bare array — three problems in one: the type varied, an array
      // is not an element so a caller could not give it props, and React only
      // tolerates a returned array because of the keys below. A fragment is a
      // single element in both cases and renders exactly the same DOM.
      if (plans.length === 0) return <>-</>;

      return (
        <>
          {plans.map((plan) => (
            <span key={plan} className="policies-page__plan-badge">
              {plan}
            </span>
          ))}
        </>
      );
    },
  },
];

const Policies = () => {
  const { selectedProperty } = useContext(PropertyStoreContext);
  const [searchQuery, setSearchQuery] = useState("");
  const [activeTab, setActiveTab] = useState(POLICY_TABS.CANCELATION);
  const [cancellationPolicies, setCancellationPolicies] = useState([]);
  const [seasonalPolicies, setSeasonalPolicies] = useState([]);
  const [loadingCancellation, setLoadingCancellation] = useState(false);
  const [loadingSeasonal, setLoadingSeasonal] = useState(false);
  const [cancellationSortDirection, setCancellationSortDirection] =
    useState(null);
  const [seasonalSortDirection, setSeasonalSortDirection] = useState(null);
  // Same source and priority as supply-launchpad (and the Promotions pages):
  // the property's first accepted currency, straight off the property
  // context — no extra fetch needed.
  const displayCurrency = selectedProperty?.acceptedCurrencies?.[0] ?? null;

  useEffect(() => {
    if (!selectedProperty?.propertyId) return;

    let cancelled = false;
    setCancellationPolicies([]);
    setSeasonalPolicies([]);
    setLoadingCancellation(true);
    setLoadingSeasonal(true);
    setSearchQuery("");
    setCancellationSortDirection(null);
    setSeasonalSortDirection(null);

    getCancellationPolicies(selectedProperty.propertyId)
      .then((response) => {
        if (cancelled) return;
        const entity = response?.data?.data?.getEntityWithRelations;
        if (
          entity?.success === false ||
          (entity?.errors && entity.errors.length > 0)
        ) {
          toast.error(
            "Failed to load cancellation policies. Please try again later."
          );
        } else {
          setCancellationPolicies(entity?.policies || []);
        }
      })
      .catch(() => {
        if (!cancelled) {
          toast.error(
            "Failed to load cancellation policies. Please try again later."
          );
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingCancellation(false);
      });

    getSeasonalPolicies(selectedProperty.propertyId)
      .then((response) => {
        if (cancelled) return;
        const entity = response?.data?.data?.getEntityWithRelations;
        if (
          entity?.success === false ||
          (entity?.errors && entity.errors.length > 0)
        ) {
          toast.error(
            "Failed to load seasonal policies. Please try again later."
          );
        } else {
          // The query returns the policy list under `policies`, matching the
          // cancellation query. Each row's own `seasonalPolicies` is the list
          // of date ranges it applies to.
          setSeasonalPolicies(entity?.policies || []);
        }
      })
      .catch(() => {
        if (!cancelled) {
          toast.error(
            "Failed to load seasonal policies. Please try again later."
          );
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingSeasonal(false);
      });

    return () => {
      cancelled = true;
    };
  }, [selectedProperty?.propertyId]);

  const handleTabClick = (index) => {
    setActiveTab(index);
    return Promise.resolve();
  };

  const handleCancellationSortToggle = () => {
    setCancellationSortDirection((prev) => getNextSortDirection(prev));
  };

  const handleSeasonalSortToggle = () => {
    setSeasonalSortDirection((prev) => getNextSortDirection(prev));
  };

  const filteredCancellationPolicies = useMemo(
    () =>
      sortPoliciesByStatus(
        filterPoliciesBySearch(cancellationPolicies, searchQuery),
        cancellationSortDirection
      ),
    [cancellationPolicies, searchQuery, cancellationSortDirection]
  );

  const filteredSeasonalPolicies = useMemo(
    () =>
      sortPoliciesByStatus(
        filterPoliciesBySearch(seasonalPolicies, searchQuery),
        seasonalSortDirection
      ),
    [seasonalPolicies, searchQuery, seasonalSortDirection]
  );

  return (
    <Grid fluid className="property-config-page">
      <PropertyConfigHeader title="Policies" />

      <PropertyPageCard
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        searchAriaLabel="Search policies"
        toolbarLeft={
          <div className="policies-page__tabs-wrapper">
            <TabsHT
              initiallyActiveTab={POLICY_TABS.CANCELATION}
              className="policies-page__tabs"
            >
              {/* Workaround: empty fragment prevents tabUtils.js from
                  treating the single TabList child as a container and
                  unwrapping it. See also: RoomtypeTabs.js */}
              <></>
              <TabsHT.TabList>
                <TabsHT.Tab onClick={handleTabClick}>
                  Cancelation Policies
                </TabsHT.Tab>
                <TabsHT.Tab onClick={handleTabClick}>
                  Seasonal Policies
                </TabsHT.Tab>
              </TabsHT.TabList>
            </TabsHT>
          </div>
        }
      >
        {activeTab === POLICY_TABS.CANCELATION && (
          <PropertyConfigTable
            columns={getCancellationColumns(
              cancellationSortDirection,
              handleCancellationSortToggle,
              displayCurrency
            )}
            data={filteredCancellationPolicies}
            rowKey="id"
            loading={loadingCancellation}
            loadingMessage="Loading cancellation policies..."
            emptyMessage="No cancellation policies found."
          />
        )}

        {activeTab === POLICY_TABS.SEASONAL && (
          <PropertyConfigTable
            columns={getSeasonalColumns(
              seasonalSortDirection,
              handleSeasonalSortToggle,
              displayCurrency
            )}
            data={filteredSeasonalPolicies}
            rowKey="id"
            loading={loadingSeasonal}
            loadingMessage="Loading seasonal policies..."
            emptyMessage="No seasonal policies found."
          />
        )}
      </PropertyPageCard>
    </Grid>
  );
};

export default Policies;
